/**
 * Plan access for Devello (Free vs Pro). Server writes `plan_tier` / `access_source`;
 * UI must not trust client-side overrides for security-sensitive operations.
 *
 * @param {Record<string, unknown> | null | undefined} profile Row from public.profiles (+ merged email)
 * @returns {boolean}
 */
export function isProEntitlement(profile) {
  if (!profile) return false;
  const accessLevel = profile.access_level ?? profile.accessLevel;
  if (accessLevel === 'pro') return true;
  const tier = profile.plan_tier ?? profile.planTier;
  if (tier === 'pro') return true;
  const legacy = profile.plan;
  return legacy === 'pro';
}

/** @param {Record<string, unknown> | null | undefined} profile */
export function isFreeEntitlement(profile) {
  return !isProEntitlement(profile);
}

/**
 * @param {Record<string, unknown> | null | undefined} profile
 * @returns {'free'|'pro'}
 */
export function effectivePlanTier(profile) {
  return isProEntitlement(profile) ? 'pro' : 'free';
}

/**
 * @param {Record<string, unknown> | null | undefined} profile
 * @returns {'manual'|'stripe'|string}
 */
export function accessSource(profile) {
  return profile?.access_source || profile?.accessSource || 'manual';
}

/**
 * @param {Record<string, unknown> | null | undefined} profile
 * @returns {boolean}
 */
export function hasEverBeenPro(profile) {
  return profile?.has_ever_been_pro === true || profile?.hasEverBeenPro === true;
}

/**
 * Free users with prior Pro history can still see Projects in read-only mode.
 * @param {Record<string, unknown> | null | undefined} profile
 * @returns {boolean}
 */
export function canViewProjects(profile) {
  return isProEntitlement(profile) || hasEverBeenPro(profile);
}

/**
 * Project mutations are strictly Pro-only.
 * @param {Record<string, unknown> | null | undefined} profile
 * @returns {boolean}
 */
export function canMutateProjects(profile) {
  return isProEntitlement(profile);
}

/**
 * Users who downgraded from Pro should land in a projects-only shell.
 * @param {Record<string, unknown> | null | undefined} profile
 * @returns {boolean}
 */
export function isDowngradedReadOnlyProfile(profile) {
  return !isProEntitlement(profile) && hasEverBeenPro(profile);
}
